import React, {useMemo} from 'react';
import cx from 'classnames';
import {useTranslation} from 'react-i18next';

import type {IconSource} from '@pexip/components';
import {Select} from '@pexip/components';
import type {MediaDeviceInfoLike, IndexedDevices} from '@pexip/media-control';
import {getDeviceId} from '@pexip/media-control';

import {mapDevicesToOptions} from '../../utils/devicesSelection';

export const DeviceSelect: React.FC<
    {
        devices: IndexedDevices;
        kind: MediaDeviceKind;
        iconType?: IconSource;
        isDisabled: boolean;
        label: string;
        selected: MediaDeviceInfoLike | undefined;
        onDeviceChange: (id: string) => void;
    } & Partial<React.ComponentProps<typeof Select>>
> = ({
    devices,
    kind,
    iconType,
    isDisabled = false,
    label,
    selected,
    onDeviceChange,
    className,
    sizeModifier = 'small',
    ...props
}) => {
    const {t} = useTranslation();

    const deviceId = selected ? getDeviceId(selected) : '';

    const options = useMemo(() => {
        let defaultLabel = '';
        switch (kind) {
            case 'audioinput': {
                defaultLabel = t(
                    'media.default-microphone-label',
                    'Default microphone',
                );
                break;
            }
            case 'videoinput': {
                defaultLabel = t(
                    'media.default-camera-label',
                    'Default camera',
                );
                break;
            }
            case 'audiooutput': {
                defaultLabel = t(
                    'media.default-speaker-label',
                    'Default speaker',
                );
                break;
            }
            default:
                break;
        }
        return mapDevicesToOptions({
            devices: devices.get({kind}),
            defaultDeviceLabel: defaultLabel,
            selectedDeviceId: deviceId,
            label,
        });
    }, [devices, kind, t, deviceId, label]);

    return (
        <Select
            value={deviceId}
            onValueChange={onDeviceChange}
            options={options}
            iconType={iconType}
            label={label}
            labelModifier="hidden"
            className={cx('mb-1', className)}
            isFullWidth
            isDisabled={isDisabled}
            sizeModifier={sizeModifier}
            {...props}
        />
    );
};
